/**
 * API Vault · Level 0 credential rotation console · v2.7.65+E101
 * ════════════════════════════════════════════════════════════════
 * Root-only UI for rotating OPENAI_API_KEY + OPENAI_TRACKING_ID
 * without touching backend .env files. Values ship encrypted to
 * Mongo + mirrored into os.environ at runtime.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, KeyRound, Save, Trash2, ShieldCheck, ShieldAlert,
  Loader2, RefreshCw, Eye, EyeOff, CheckCircle2, XCircle,
} from "lucide-react";

const KEY_META = {
  OPENAI_API_KEY: {
    label:       "OpenAI API Key",
    placeholder: "sk-...",
    description: "Used for premium AI features when Emergent's Claude budget isn't sufficient. Rotated by ops.",
    can_validate: true,
    sensitive:    true,
  },
  OPENAI_TRACKING_ID: {
    label:       "OpenAI Tracking ID (org / project)",
    placeholder: "org-abc123 or proj_xyz456",
    description: "Optional. Tags OpenAI usage to a specific org/project for billing splits.",
    can_validate: true,
    sensitive:    false,
  },
};

export default function ApiVaultPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/api-vault/list");
      setEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("API Vault is Level 0 only · sign in as Derick or Steven.");
      } else {
        toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const entryOf = (key) => entries.find((e) => e.key_name === key);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-6"
         data-testid="api-vault-page">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Link to="/admin"
                className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-400 text-xs font-bold uppercase tracking-widest">
            <ArrowLeft className="h-4 w-4"/>Admin Console
          </Link>
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-rose-500/10 border border-rose-500/40 flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-rose-400"/>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight">API Vault</h1>
              <div className="text-[11px] text-zinc-400 uppercase tracking-widest font-bold">
                Level 0 · Root only · encrypted at rest
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
                  data-testid="api-vault-reload"
                  className="text-xs border-zinc-700">
            {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3 mr-1"/>}
            Reload
          </Button>
        </div>

        <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-3 text-[11px] text-rose-200">
          <ShieldAlert className="h-3.5 w-3.5 inline mr-1"/>
          Keys are Fernet-encrypted with a JWT-derived symmetric key. Only Derick + Steven can read/write this page. Rotating a key mirrors the new value into <code className="font-mono">os.environ</code> instantly — no pod restart.
        </div>

        {Object.entries(KEY_META).map(([keyName, meta]) => (
          <VaultRow
            key={keyName}
            keyName={keyName}
            meta={meta}
            entry={entryOf(keyName)}
            busy={busyKey === keyName}
            setBusy={(v) => setBusyKey(v ? keyName : null)}
            onChanged={load}
          />
        ))}
      </div>
    </div>
  );
}

function VaultRow({ keyName, meta, entry, busy, setBusy, onChanged }) {
  const [value, setValue] = useState("");
  const [showValue, setShowValue] = useState(false);

  const save = async () => {
    if (!value.trim()) { toast.error("Value can't be empty"); return; }
    setBusy(true);
    try {
      await adminApi.post("/admin/api-vault/upsert", {
        key_name: keyName, value: value.trim(),
      });
      toast.success(`${meta.label} stored · will validate now`);
      setValue("");
      // Auto-validate right after save
      if (meta.can_validate) {
        try {
          const { data } = await adminApi.post("/admin/api-vault/validate", { key_name: keyName });
          if (data?.status === "Active") toast.success(`${meta.label} validated · Active`);
          else toast.warning(`Validation returned: ${data?.status} · ${data?.detail?.error || ""}`);
        } catch (_e) { /* silent */ }
      }
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/api-vault/validate", { key_name: keyName });
      if (data?.status === "Active") toast.success(`${meta.label} · Active`);
      else toast.error(`${meta.label} · ${data?.status || "Unknown"} · ${data?.detail?.error || ""}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validate failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete stored ${meta.label}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/api-vault/${keyName}`);
      toast.success(`${meta.label} removed`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const status = entry?.validation_status;
  const statusColor = status === "Active"
    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
    : status === "Invalid"
      ? "bg-rose-500/20 text-rose-300 border-rose-500/40"
      : "bg-zinc-800 text-zinc-400 border-zinc-700";

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={`vault-row-${keyName}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold text-sm">{meta.label}</span>
            {entry && (
              <Badge className={`border ${statusColor} text-[9px] uppercase tracking-widest font-bold`}
                     data-testid={`vault-status-${keyName}`}>
                {status || "Untested"}
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-zinc-400">{meta.description}</div>
          {entry && (
            <div className="mt-1.5 flex items-center gap-3 text-[10px] text-zinc-500 font-mono">
              <span>Stored: <span className="text-zinc-300">{entry.masked_value}</span></span>
              <span>Updated: {(entry.updated_at || "").replace("T", " ").slice(0, 19)}</span>
              {entry.updated_by && <span>By: {entry.updated_by}</span>}
            </div>
          )}
        </div>
        {entry && (
          <Button size="sm" variant="ghost" onClick={remove} disabled={busy}
                  data-testid={`vault-delete-${keyName}`}
                  className="text-rose-400 hover:text-rose-300 h-7 text-xs">
            <Trash2 className="h-3 w-3 mr-1"/>Remove
          </Button>
        )}
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            {entry ? "Rotate value" : "Set initial value"}
          </div>
          <div className="relative">
            <Input
              type={showValue || !meta.sensitive ? "text" : "password"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={meta.placeholder}
              disabled={busy}
              autoComplete="off"
              spellCheck={false}
              data-testid={`vault-input-${keyName}`}
              className="bg-zinc-950 border-zinc-700 pr-9 text-sm font-mono"
            />
            {meta.sensitive && (
              <button
                type="button"
                onClick={() => setShowValue((s) => !s)}
                data-testid={`vault-toggle-visibility-${keyName}`}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                aria-label={showValue ? "Hide value" : "Show value"}
              >
                {showValue ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
              </button>
            )}
          </div>
        </div>
        <Button
          onClick={save}
          disabled={busy || !value.trim()}
          data-testid={`vault-save-${keyName}`}
          className="bg-rose-500 hover:bg-rose-400 text-zinc-950 font-bold h-10 text-xs"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Save className="h-3 w-3 mr-1"/>Update</>}
        </Button>
        {entry && meta.can_validate && (
          <Button
            variant="outline"
            onClick={validate}
            disabled={busy}
            data-testid={`vault-validate-${keyName}`}
            className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 h-10 text-xs"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin"/> :
              status === "Active" ? <CheckCircle2 className="h-3 w-3 mr-1"/> :
              status === "Invalid" ? <XCircle className="h-3 w-3 mr-1"/> :
              <ShieldCheck className="h-3 w-3 mr-1"/>}
            Validate
          </Button>
        )}
      </div>
    </Card>
  );
}
